import { NativeSelect, NativeSelectProps, NumberInput, NumberInputProps } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { Unit } from 'types/products'
import { getUnitName } from 'utils'

type NumberWithUnitValues = {
  unit: Unit
} & {
  [K in string as K extends `${string}Amount` ? K : K extends 'amount' ? K : never]: number
}

interface Props<Values extends NumberWithUnitValues> extends Omit<NumberInputProps, 'form'> {
  form: UseFormReturnType<Values>
  availableUnits?: Array<Unit>
  disabledUnit?: boolean
  amountFieldName?: string
}

export default function NumberWithUnitInput<Values extends NumberWithUnitValues>({
  form,
  availableUnits,
  disabledUnit,
  amountFieldName = 'amount',
  description,
  ...numberInputProps
}: Props<Values>) {
  const isError = !!form.errors[amountFieldName]

  const unit = form.getValues().unit
  const suffix = ' ' + getUnitName(unit).short

  let defaultPlaceholder: string
  if (unit === Unit.PIECE) {
    defaultPlaceholder = `10${suffix}`
  } else if (unit === Unit.METER) {
    defaultPlaceholder = `10.65${suffix}`
  } else {
    defaultPlaceholder = `40${suffix}`
  }

  return (
    <NumberInput
      allowNegative={false}
      placeholder={numberInputProps.placeholder ?? defaultPlaceholder}
      suffix={suffix}
      allowDecimal={unit === Unit.METER}
      decimalScale={2}
      fixedDecimalScale
      hideControls
      decimalSeparator=","
      withAsterisk
      description={description}
      rightSection={
        <UnitSelect
          isDescription={!!description}
          isError={isError}
          availableUnits={availableUnits}
          disabled={disabledUnit}
          // @ts-ignore
          resetAmount={() => form.setFieldValue(amountFieldName, '')}
          {...form.getInputProps('unit')}
        />
      }
      rightSectionWidth={120}
      {...numberInputProps}
      {...form.getInputProps(amountFieldName)}
    />
  )
}

interface UnitSelectProps extends NativeSelectProps {
  isError: boolean
  availableUnits?: Array<Unit>
  resetAmount: () => void
  isDescription: boolean
}

function UnitSelect({ isError, availableUnits, resetAmount, onChange, isDescription, ...props }: UnitSelectProps) {
  const data: Array<{ value: Unit; label: string }> = [
    { value: Unit.METER, label: 'Metr' },
    // { value: Unit.CENTIMETER, label: 'Centymetr' },
    { value: Unit.PIECE, label: 'Sztuka' },
  ]
  const filteredData = availableUnits ? data.filter((unit) => availableUnits.includes(unit.value)) : data

  const descriptionMargin = isDescription ? 0 : 5

  return (
    <NativeSelect
      data={filteredData}
      rightSectionWidth={28}
      onChange={(e) => {
        onChange?.(e)
        resetAmount()
      }}
      styles={{
        input: {
          fontWeight: 500,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          width: 120,
          marginRight: -2,
          marginTop: (isError ? 0 : -5) + descriptionMargin,
        },
      }}
      {...props}
    />
  )
}
